import { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { ColumnVisibility, QMenuButton, QMenuItemType, SavedViews, useCurrentUser } from '@qualio/ui-components';
import { getAllUrlParameter, getUrlParameter } from '../../../../utils/searchParamUtils';
import { useSearchParams } from 'react-router-dom';
import { EMPTY_FILTER_VALUE, ExportFormat, QualityEventsGet, RelatedRecordRef } from '../../../../types';
import { useCreateExport } from '../../../../hooks/useCreateExport';
import { getUtcStartOfDay, getUtcEndOfDay } from '../../../../utils/datetimeUtils';
import { decodeQri } from '../../../../utils/relatedRecords';
import { EVENT_TABLE_COLUMN_IDS } from '../../../../components/EventTable/columns';
import { resolveExportColumns } from '../../../../utils/exportColumns';
import { medtechApi } from '../../../../api';
import { eventsListSavedViewsTableKey } from '../../../../types/common/constants';
import { useFlags } from '../../../../external/LaunchDarklyAdapter';
import { parseViewId, VIEW_ID_PARAM, viewDataToSearchParams } from '../../../../utils/savedViewParams';

const ExportButton = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { mutate: createExport } = useCreateExport();
  const { tz } = useCurrentUser();
  const isSavedViewsEnabled = useFlags('listSavedViews');
  const isEventCollaboratorsEnabled = useFlags('eventCollaborators');
  const isMultiSelectEventsListEnabled = useFlags('multiSelectEventsList');
  // Absent visibility means the feature flag is off upstream, and the payload must stay as it was.
  const visibility = ColumnVisibility.useColumnVisibilityMaybe()?.resolvedVisibility;
  const appliedViewId = parseViewId(searchParams.get(VIEW_ID_PARAM));
  const shouldResolveAppliedView = isSavedViewsEnabled && appliedViewId !== undefined;
  const { data: savedViews, isLoading: isLoadingSavedViews } = SavedViews.useSavedViews(
    medtechApi,
    eventsListSavedViewsTableKey,
    shouldResolveAppliedView,
  );
  const ownView = savedViews?.find((view) => view.id === appliedViewId);
  const { data: sharedView, isLoading: isLoadingSharedView } = SavedViews.useSavedView(
    medtechApi,
    eventsListSavedViewsTableKey,
    appliedViewId,
    shouldResolveAppliedView && !isLoadingSavedViews && !ownView,
  );
  const appliedView = ownView ?? sharedView;
  const filterSource = useMemo(
    () => (appliedView ? viewDataToSearchParams(appliedView.view_data) : searchParams),
    [appliedView, searchParams],
  );

  const createQualityEventsRequest = useCallback((): QualityEventsGet => {
    const emptyValues: Array<any> = ['', null, undefined];

    const relatedRecordQris = getAllUrlParameter(filterSource, 'related_records');
    const relatedRecords: RelatedRecordRef[] | undefined = relatedRecordQris.length
      ? relatedRecordQris.map((qri) => {
          const { domain, type, id, version_id } = decodeQri(decodeURIComponent(qri));
          return { domain, type, id, ...(version_id ? { version_id } : {}) };
        })
      : undefined;

    const params: QualityEventsGet = {
      status: getAllUrlParameter(filterSource, 'status'),
      owner_id: getUrlParameter(filterSource, 'owner_id'),
      owner_ids: getAllUrlParameter(filterSource, 'owner_ids'),
      event_type: getUrlParameter(filterSource, 'event_type'),
      event_types: getAllUrlParameter(filterSource, 'event_types'),
      rootcause_ids: getAllUrlParameter(filterSource, 'rootcause_ids'),
      product_id: getUrlParameter(filterSource, 'product_id'),
      product_ids: getAllUrlParameter(filterSource, 'product_ids'),
      filter_tags: getAllUrlParameter(filterSource, 'filter_tags'),
      is_overdue: getUrlParameter(filterSource, 'is_overdue'),
      created_at_start_date: getUtcStartOfDay(getUrlParameter(filterSource, 'created_at_start_date') ?? undefined, tz),
      created_at_end_date: getUtcEndOfDay(getUrlParameter(filterSource, 'created_at_end_date') ?? undefined, tz),
      // due_at is a calendar date, not an instant — UTC day boundaries, no viewer-tz localisation.
      due_at_start_date: getUtcStartOfDay(getUrlParameter(filterSource, 'due_at_start_date') ?? undefined),
      due_at_end_date: getUtcEndOfDay(getUrlParameter(filterSource, 'due_at_end_date') ?? undefined),
      resolved_at_start_date: getUtcStartOfDay(
        getUrlParameter(filterSource, 'resolved_at_start_date') ?? undefined,
        tz,
      ),
      resolved_at_end_date: getUtcEndOfDay(getUrlParameter(filterSource, 'resolved_at_end_date') ?? undefined, tz),
      // Preserve the reserved EMPTY_FILTER_VALUE sentinel (only real ids are numeric),
      // so the export payload matches the search path instead of coercing it to NaN->null.
      severity_ids: getAllUrlParameter(filterSource, 'severity_ids').map((v) =>
        v === EMPTY_FILTER_VALUE ? v : Number(v),
      ),
      probability_ids: getAllUrlParameter(filterSource, 'probability_ids').map((v) =>
        v === EMPTY_FILTER_VALUE ? v : Number(v),
      ),
      order_by: getUrlParameter(filterSource, 'order_by'),
      term: getUrlParameter(filterSource, 'term'),
      related_records: relatedRecords,
    };
    return Object.fromEntries(
      Object.entries(params).filter(([, v]) => {
        return !(
          emptyValues.includes(v) ||
          (Array.isArray(v) && (v.length === 0 || v.some((item) => emptyValues.includes(item))))
        );
      }),
    );
  }, [filterSource, tz]);

  const exportMenuOptions = [
    {
      id: 'xlsx',
      label: t('common.exports.xlsx'),
      'data-cy': 'event-list-export-xlsx',
    },
    {
      id: 'pdf',
      label: t('common.exports.pdf'),
      'data-cy': 'event-list-export-pdf',
    },
  ];

  const handleExportClick = async (exportType: QMenuItemType) => {
    const filterParams = createQualityEventsRequest();
    const format = exportType.id as ExportFormat;
    // Collaborators is visible by default even where the table doesn't render it.
    const columnIds =
      isEventCollaboratorsEnabled && isMultiSelectEventsListEnabled
        ? EVENT_TABLE_COLUMN_IDS
        : EVENT_TABLE_COLUMN_IDS.filter((id) => id !== 'collaborators');
    const columns = visibility && resolveExportColumns(columnIds, visibility);
    // The hook drops columns for xlsx inside, which always contains every column.
    createExport({ ...filterParams, format, ...(columns ? { columns } : {}) });
  };

  return (
    <QMenuButton
      buttonLabel={t('common.actions.export')}
      variant="outline"
      data-cy="export-button"
      items={exportMenuOptions}
      onItemClick={handleExportClick}
      isDisabled={isLoadingSavedViews || isLoadingSharedView}
    />
  );
};

export default ExportButton;
